import { Dispatch, SetStateAction } from 'react'
import { useMutation } from 'react-query'
import { config } from 'config'
import { AccessRequestState, LoginQueryResult, LoginResponse, Profile } from 'types/login'
import { callAxios, CustomAxiosError } from 'utils/api'

const { SECURE_BASE_URL } = config!

const login = async (params: Profile): Promise<LoginQueryResult> => {
  const { response: loginResponse } = await callAxios<{
    response: LoginQueryResult
  }>({
    url: `${SECURE_BASE_URL}/userDetails`,
    method: 'POST',
    data: {
      googleAccountId: params.googleId,
    },
  })

  return { ...params, ...loginResponse }
}

type UseLoginProps = {
  onLoginSuccess(data: Partial<LoginResponse & Profile>): void
  setAccessRequestState: Dispatch<SetStateAction<AccessRequestState>>
  logOut: () => Promise<void>
}

export const useLogin = (props: UseLoginProps) => {
  const { onLoginSuccess, setAccessRequestState, logOut } = props

  const {
    status: loginStatus,
    error: loginError,
    mutate: mutateLogin,
  } = useMutation<LoginQueryResult, CustomAxiosError, Profile>(login, {
    onSuccess: (data) => {
      onLoginSuccess(data)
    },
    onError: (error, variables) => {
      if (error.errorCode === 404) {
        const data = error.errorMoreInfo
        if (data && typeof data === 'object') {
          setAccessRequestState({
            requestUser: {
              ...variables,
              supplierDetails: {
                supplierName: data?.supplierName,
                supplierType: data?.supplierType,
                oracleId: data?.oracleId,
                relatedGroup: data?.relatedGroup,
                roles: data?.roles,
              },
              jobRole: data?.jobRole ?? '',
              workMail: data?.workMail ?? '',
            },
            accessRequest: 'request_access',
          })
        } else {
          setAccessRequestState({
            requestUser: null,
            accessRequest: 'unknown_access',
          })
        }
      } else if (
        error.errorCode === 422 &&
        error.errorMoreInfo?.includes('not found') &&
        variables.morrisonMailId.endsWith('@morrisonsplc.co.uk')
      ) {
        setAccessRequestState({
          requestUser: {
            ...variables,
          },
          accessRequest: 'request_access',
        })
      } else if (error.errorCode === 403 || error.errorCode === 422) {
        setAccessRequestState({
          requestUser: null,
          accessRequest: 'disabled_access',
        })
      } else {
        setAccessRequestState({
          requestUser: null,
          accessRequest: 'unknown_access',
        })
      }
      logOut()
    },
  })

  return { loginStatus, loginError, mutateLogin }
}
